/**
 * ShopRoute — `/butikk`, Butikk-fanen i Premier (#156, steg 6b; Stell_Butikk).
 *
 * Det første poengsluket uten godkjenning: barnet kjøper pynt til avataren
 * med en gang. Fra toppen:
 *   - PremierFrame: ScreenHeader «Premier» med saldopillen, som lenker til
 *     Lommebok (`/poeng`), og underfanene rett under;
 *   - «Avataren din»: den sammensatte avataren (D2 beholder piksel-/
 *     pyntavataren her) og det som er i bruk, per type;
 *   - typefilteret som `Chip`-er, bare for typene katalogen har. Et trykk
 *     filtrerer, et nytt trykk viser alt igjen;
 *   - katalogen i to kolonner (tre på brede skjermer), og fotnoten.
 *
 * Skissens «Vises også i familieflyten» venter på ny oppførsel 8 og er utelatt.
 *
 * Kjøp: «Kjøp» → useBuyCosmetic → konfetti ved suksess (ConfettiBurst med ny
 * nøkkel), og spørringene invalideres så kortet går over til «Bruk» og
 * saldoen synker. En 422 vises som et banner (for lite poeng / for ung).
 *
 * Auth + viewer: GET /api/auth/me gir medlemmet (som RewardsStoreRoute).
 * Katalogen og garderoben hentes for det; serveren er porten (et barn kan
 * bare lese sine egne).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { COSMETIC_KINDS, type CosmeticKind } from '@stell/shared';
import { EmptyState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { Chip } from '../../components/primitives/Chip';
import { ConfettiBurst } from '../../components/primitives/ConfettiBurst';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { BalancePill, PremierFrame } from '../rewards/PremierFrame';
import { useBalance } from '../points/usePointsQueries';
import { CosmeticAvatar, equippedLayers } from './CosmeticAvatar';
import { CosmeticCard } from './CosmeticCard';
import {
  useCosmeticCatalog,
  useOwnedCosmetics,
  useBuyCosmetic,
  useEquipCosmetic,
  type FetchError,
} from './useCosmetics';

interface SessionMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  color_token: string;
}

const COLOR_SLOTS: readonly ColorSlot[] = [
  'm-terracotta',
  'm-berry',
  'm-ochre',
  'm-sage',
  'm-sky',
  'm-plum',
];

function asColorSlot(token: string | undefined): ColorSlot {
  return (COLOR_SLOTS as readonly string[]).includes(token ?? '')
    ? (token as ColorSlot)
    : 'm-sky';
}

function useSession(): { viewer: SessionMember | null; loaded: boolean } {
  const [viewer, setViewer] = useState<SessionMember | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!r.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (!cancelled) {
          setViewer(body.member ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { viewer, loaded };
}

function errorKey(err: FetchError | null): string | null {
  if (!err) return null;
  const body = err.body as { error?: string } | undefined;
  switch (body?.error) {
    case 'insufficient_points':
      return 'shop.error.insufficient';
    case 'age_locked':
      return 'shop.error.ageLocked';
    case 'already_owned':
      return 'shop.error.alreadyOwned';
    default:
      return 'shop.error.generic';
  }
}

export function ShopRoute(): ReactNode {
  const { t } = useTranslation();
  const { viewer, loaded } = useSession();
  const memberId = viewer?.id ?? null;
  const colorSlot = asColorSlot(viewer?.color_token);

  const catalogQuery = useCosmeticCatalog(memberId);
  const ownedQuery = useOwnedCosmetics(memberId);
  const balanceQuery = useBalance(memberId ?? '', !!memberId);

  const buy = useBuyCosmetic(memberId ?? '');
  const equip = useEquipCosmetic(memberId ?? '');

  // Confetti remount key — bumped on every successful purchase.
  const [burstKey, setBurstKey] = useState(0);
  const [errKey, setErrKey] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<CosmeticKind | null>(null);

  const equipped = useMemo(() => (ownedQuery.data ?? []).filter((o) => o.equipped), [ownedQuery.data]);
  const layers = useMemo(() => equippedLayers(equipped), [equipped]);

  if (!loaded) {
    return (
      <PremierFrame testId="shop-loading">
        <p className="m-0 text-sm text-ink-2">{t('shop.loading')}</p>
      </PremierFrame>
    );
  }

  if (!viewer) {
    return (
      <PremierFrame testId="shop-403">
        <p className="m-0 py-8 text-center text-sm text-ink-2">{t('shop.notAuthFallback')}</p>
      </PremierFrame>
    );
  }

  const catalog = catalogQuery.data ?? [];
  // Ukjent saldo (henter eller feilet) er ikke 0: kortene viser da bare prisen.
  const balance = balanceQuery.data;
  const kinds = COSMETIC_KINDS.filter((k) => catalog.some((item) => item.kind === k));
  // Et filter på en type som har forsvunnet fra katalogen, nullstilles, så det
  // ikke kommer tilbake av seg selv når typen dukker opp igjen.
  if (kindFilter !== null && catalogQuery.isSuccess && !kinds.includes(kindFilter)) setKindFilter(null);
  const shown = kindFilter === null ? catalog : catalog.filter((item) => item.kind === kindFilter);

  // Én ting i bruk per type (cosmetics.service.equipItem), i samme rekkefølge som filteret.
  const summary = COSMETIC_KINDS.flatMap((kind) =>
    equipped
      .filter((o) => o.kind === kind)
      .map((o) => t('shop.preview.item', { kind: t(`shop.kind.${kind}`), name: o.name })),
  ).join(' · ');
  // Kjøpsfeil fra handlingene først; ellers en henting som feilet. Uten saldo
  // kan ingenting kjøpes, så det skal ikke skje i stillhet.
  const bannerKey =
    errKey ?? (balanceQuery.isError || catalogQuery.isError ? 'shop.error.generic' : null);

  const handleBuy = (itemId: string): void => {
    setErrKey(null);
    buy.mutate(itemId, {
      onSuccess: () => setBurstKey((k) => k + 1),
      onError: (err) => setErrKey(errorKey(err)),
    });
  };

  const handleEquip = (itemId: string, equipped: boolean): void => {
    setErrKey(null);
    equip.mutate({ itemId, equipped }, { onError: (err) => setErrKey(errorKey(err)) });
  };

  const busy = buy.isPending || equip.isPending;

  return (
    <PremierFrame testId="shop" trailing={<BalancePill balance={balance} data-testid="shop-balance" />}>
      {/* Den sammensatte avataren og det som er i bruk */}
      <section
        data-testid="shop-preview"
        aria-labelledby="shop-preview-title"
        className={`${GLASS_SURFACE} flex items-center gap-4 rounded-xl p-3.5 shadow-md md:max-w-xl`}
      >
        <CosmeticAvatar colorSlot={colorSlot} equipped={layers} size={84} className="shrink-0" />
        <div className="min-w-0 flex-1">
          {/* Ikke en overskrift: navnene på kortene er h2, som i skissen. */}
          <p id="shop-preview-title" className="m-0 text-heading-s font-extrabold text-ink">
            {t('shop.preview.title')}
          </p>
          <p data-testid="shop-preview-equipped" className="m-0 text-control leading-snug text-ink-2">
            {summary || t('shop.preview.none')}
          </p>
        </div>
      </section>

      {bannerKey ? (
        <div
          data-testid="shop-error"
          role="alert"
          className="rounded-md border border-warn-line bg-warn-bg px-4 py-3 text-sm font-bold text-warn"
        >
          {t(bannerKey)}
        </div>
      ) : null}

      {catalogQuery.isPending ? (
        <p data-testid="shop-catalog-loading" className="m-0 text-sm text-ink-2">
          {t('shop.loading')}
        </p>
      ) : catalog.length === 0 ? (
        <section data-testid="shop-empty">
          <EmptyState
            variant="inline"
            heading={t('shop.empty.heading')}
            body={t('shop.empty.body')}
          />
        </section>
      ) : (
        <>
          {kinds.length > 1 ? (
            <div
              role="group"
              aria-label={t('shop.filter.label')}
              data-testid="shop-filter"
              className="flex flex-wrap gap-1.5"
            >
              {kinds.map((kind) => (
                <Chip
                  key={kind}
                  selected={kindFilter === kind}
                  data-testid={`shop-filter-${kind}`}
                  onClick={() => setKindFilter(kindFilter === kind ? null : kind)}
                >
                  {t(`shop.kind.${kind}`)}
                </Chip>
              ))}
            </div>
          ) : null}

          <section data-testid="shop-grid" className="grid grid-cols-2 gap-2.5 lg:grid-cols-3">
            {shown.map((item) => (
              <CosmeticCard
                key={item.id}
                item={item}
                balance={balance}
                colorSlot={colorSlot}
                busy={busy}
                onBuy={() => handleBuy(item.id)}
                onEquip={(on) => handleEquip(item.id, on)}
              />
            ))}
          </section>

          <p className="m-0 text-center text-caption text-ink-2">{t('shop.footnote')}</p>
        </>
      )}

      {/* Fire-and-forget confetti on a successful purchase. */}
      {burstKey > 0 ? (
        <ConfettiBurst key={burstKey} tier="l3" viewport="phone" />
      ) : null}
    </PremierFrame>
  );
}
